import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as rxjs from 'rxjs'

import * as me from '../me'

import * as gql from './gql'

/**
 * API key management for the zone the user is currently in.
 *
 * __NOTE:__ a generated key's plaintext deliberately never enters this
 * service's state. It is handed to the caller of {@link generate} and nowhere
 * else, so the only place it can live is the component that is showing it, and
 * it dies when that component does
 */
@ngCore.Injectable({ providedIn: 'root' })
export class ApiKeys {
  /** @see {@link me.Feature} */
  private readonly _meFeature = ngCore.inject(me.Feature)

  /** Zone the slideout is acting on */
  private readonly _zoneContextSg =
    this._meFeature.contexts.selectors.zoneContext.asSignal

  /** Keys loaded for the current zone */
  public readonly keysSg = ngCore.signal<readonly ents.core.admin.ApiKey[]>([])

  /** True while the list is being loaded or mutated */
  public readonly loadingSg = ngCore.signal(false)

  /** Set when the last operation failed, so the slideout can surface it */
  public readonly errorSg = ngCore.signal<string | undefined>(undefined)

  /**
   * Whether the API Access panel is reachable at all.
   *
   * Deliberately broader than {@link canGenerateSg}: listing and revoking are
   * not gated on the generate permission, so a user who loses it can still
   * turn off keys they already hold
   */
  public readonly canAccessSg =
    this._meFeature.contexts.selectors.canAccessApiKeys.asSignal

  /** Whether the current user may mint new keys in the current zone */
  public readonly canGenerateSg =
    this._meFeature.contexts.selectors.canGenerateApiKey.asSignal

  /** Loads the current user's keys for the active zone */
  public load(): void {
    const context = this._currentZone()
    if (!context) {
      this.keysSg.set([])
      return
    }

    this.loadingSg.set(true)
    this.errorSg.set(undefined)
    rxjs.firstValueFrom(gql.queries.apiKeys.execute(context)).then(
      keys => {
        this.keysSg.set(keys)
        this.loadingSg.set(false)
      },
      (err: unknown) => {
        this.errorSg.set(this._describe(err, 'Could not load API keys'))
        this.loadingSg.set(false)
      },
    )
  }

  /**
   * Mints a new key and returns it, plaintext included, exactly once.
   *
   * The caller is responsible for showing the plaintext and then dropping it —
   * it is never stored here, and cannot be retrieved again
   */
  public async generate(
    input: ents.core.admin.ApiKey.Input,
  ): Promise<ents.core.admin.ApiKey.Generated | undefined> {
    const context = this._currentZone()
    if (!context) {
      return undefined
    }

    this.loadingSg.set(true)
    this.errorSg.set(undefined)
    try {
      const generated = await rxjs.firstValueFrom(
        gql.mutations.generateApiKey.execute({
          ...context,
          // `AWSDateTime` is an ISO string; the entity carries epoch millis
          expiresAt:
            input.expiresAt === undefined
              ? null
              : new Date(input.expiresAt).toISOString(),
          name: input.name ?? '',
        }),
      )
      // Only the metadata goes into state — never `generated.key`
      this.keysSg.update(keys => [generated.apiKey, ...keys])
      return generated
    } catch (err: unknown) {
      this.errorSg.set(this._describe(err, 'Could not generate an API key'))
      return undefined
    } finally {
      this.loadingSg.set(false)
    }
  }

  /** Permanently disables one of the current user's keys */
  public async revoke(id: ents.core.admin.ApiKey.Id): Promise<void> {
    const context = this._currentZone()
    if (!context) {
      return
    }

    this.loadingSg.set(true)
    this.errorSg.set(undefined)
    try {
      await rxjs.firstValueFrom(
        gql.mutations.revokeApiKey.execute({ ...context, id }),
      )
      // Revoked keys stay in the list, greyed out, so the user can still see
      // that the key existed and when it was last used
      this.keysSg.update(keys =>
        keys.map(key => (key.id === id ? { ...key, enabled: false } : key)),
      )
    } catch (err: unknown) {
      this.errorSg.set(this._describe(err, 'Could not revoke the API key'))
    } finally {
      this.loadingSg.set(false)
    }
  }

  /** The zone whose keys are being managed */
  private _currentZone(): ents.core.admin.Context.ForZone | undefined {
    return this._zoneContextSg()
  }

  /** Turns an unknown rejection into something worth showing a user */
  private _describe(err: unknown, fallback: string): string {
    return err instanceof Error && err.message ? err.message : fallback
  }
}
